/**
 * Extension Error Handler
 *
 * Provides utilities to handle and suppress common browser extension errors
 * that don't affect the application functionality but clutter the console.
 */

import { environment } from 'src/environments/environment';

interface DevExpectedErrorRule {
  id: string;
  note: string;
  matches: (message: string) => boolean;
}

const DEV_EXPECTED_ERROR_RULES: DevExpectedErrorRule[] = [
  {
    id: 'dev-appinsights-401',
    note: 'Application Insights auth errors can occur in local/dev and are non-blocking.',
    matches: (message: string) =>
      message.includes('applicationinsights') &&
      (message.includes('401') || message.includes('unauthorized')),
  },
  {
    id: 'dev-appinsights-key-401',
    note: 'GetAppInsightsKey 401 can occur in local/dev before auth context is fully established.',
    matches: (message: string) =>
      message.includes('getappinsightskey') &&
      (message.includes('401') || message.includes('unauthorized')),
  },
  {
    id: 'dev-tenable-503',
    note: 'Tenable 503 responses are expected in dev when upstream scanner integrations are unavailable.',
    matches: (message: string) =>
      message.includes('tenable') &&
      (message.includes('503') || message.includes('service unavailable')),
  },
];

const emittedDevExpectedNotices = new Set<string>();

function stringifyLogArgs(args: any[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') {
        return arg;
      }

      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ')
    .toLowerCase();
}

function classifyDevExpectedError(message: string): DevExpectedErrorRule | null {
  for (const rule of DEV_EXPECTED_ERROR_RULES) {
    if (rule.matches(message)) {
      return rule;
    }
  }

  return null;
}

/**
 * Initializes global error handling for browser extension conflicts
 * Call this early in the application lifecycle (e.g., main.ts or app.component.ts)
 */
export function initializeExtensionErrorHandling(): void {
  // SSR guard - only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Suppress common extension errors that don't affect functionality
  const originalConsoleError = console.error;
  const isDevEnvironment = !environment.production;

  console.error = (...args: any[]) => {
    const message = stringifyLogArgs(args);

    if (isDevEnvironment) {
      const expectedDevError = classifyDevExpectedError(message);
      if (expectedDevError) {
        if (!emittedDevExpectedNotices.has(expectedDevError.id)) {
          emittedDevExpectedNotices.add(expectedDevError.id);
          console.warn(`[DEV EXPECTED] ${expectedDevError.note}`);
        }
        return;
      }
    }

    // Filter out known harmless extension errors
    const ignoredPatterns = [
      'invalid frameid for foreground frameid',
      'could not establish connection. receiving end does not exist',
      'extension context invalidated',
      'the message port closed before a response was received',
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'uncaught typeerror: cannot read properties of null', // Common extension injection error
      'failed to decrypt configuration', // Only ignore if it contains extension context
      'company context not available', // Only if from extension context
      'timeout', // Only extension-related timeouts
    ];

    const shouldIgnore = ignoredPatterns.some((pattern) =>
      message.includes(pattern),
    );

    // Additional check for application errors that should NOT be ignored.
    // Keep these specific — a bare 'configuration' pattern previously collided
    // with benign Application Insights "connection string/configuration" log
    // lines and forced the noisy "🔍 Application Error" banner in dev (#498).
    // The specific 'failed to decrypt configuration' entry already covers the
    // real configuration failure we care about.
    const applicationErrorPatterns = [
      'failed to decrypt configuration',
      'company context not available',
      'firestore',
      'authentication',
    ];

    const isApplicationError = applicationErrorPatterns.some((pattern) =>
      message.includes(pattern),
    );

    // Log application errors with context, suppress extension errors
    if (!shouldIgnore || isApplicationError) {
      // If it's an application error, provide additional context
      if (isApplicationError) {
        originalConsoleError.apply(console, [
          '🔍 Application Error (not extension-related):',
          ...args,
          '\n💡 Check authentication, configuration services, and network connectivity'
        ]);
      } else {
        originalConsoleError.apply(console, args);
      }
    }
  };

  // Handle unhandled promise rejections from extensions
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message?.toLowerCase() || '';
    const stack = event.reason?.stack?.toLowerCase() || '';
    const combinedMessage = `${message} ${stack}`;

    if (isDevEnvironment) {
      const expectedDevError = classifyDevExpectedError(combinedMessage);
      if (expectedDevError) {
        if (!emittedDevExpectedNotices.has(`${expectedDevError.id}-promise`)) {
          emittedDevExpectedNotices.add(`${expectedDevError.id}-promise`);
          console.warn(`[DEV EXPECTED] ${expectedDevError.note}`);
        }
        event.preventDefault();
        return;
      }
    }

    const extensionErrorPatterns = [
      'invalid frameid',
      'extension context invalidated',
      'receiving end does not exist',
      'chrome-extension',
      'moz-extension',
      'safari-extension',
    ];

    const applicationErrorPatterns = [
      'failed to decrypt configuration',
      'company context not available',
      'timeout',
      'firestore',
      'authentication failed',
      'tenable',
    ];

    const isExtensionError = extensionErrorPatterns.some((pattern) =>
      message.includes(pattern) || stack.includes(pattern),
    );

    const isApplicationError = applicationErrorPatterns.some((pattern) =>
      message.includes(pattern) || stack.includes(pattern),
    );

    if (isExtensionError && !isApplicationError) {
      event.preventDefault(); // Prevent the error from being logged
    } else if (isApplicationError) {
      // Log application errors with helpful context
      console.error('🔍 Unhandled Application Promise Rejection:', {
        reason: event.reason,
        suggestion: 'Check service initialization order and authentication state'
      });
      // Don't prevent - let it bubble up for proper error handling
    }
  });
}

/**
 * Safely checks if code is running in an extension context
 */
export function isExtensionContext(): boolean {
  try {
    return !!(
      (window as any).chrome?.extension ||
      (window as any).browser?.extension ||
      (window as any).safari?.extension
    );
  } catch {
    return false;
  }
}

/**
 * Provides a safe wrapper for operations that might conflict with extensions
 */
export function safeExtensionOperation<T>(
  operation: () => T,
  fallback?: T,
): T | undefined {
  try {
    return operation();
  } catch (error) {
    const message = (error as Error)?.message?.toLowerCase() || '';

    if (message.includes('frameid') || message.includes('extension')) {
      // Silent fail for extension-related errors
      return fallback;
    }

    // Re-throw non-extension errors
    throw error;
  }
}
